import type {ColorMap} from '@pinecast/styles';
import styled from '@pinecast/styles';
import * as React from 'react';

import {DEFAULT_FONT} from './constants';
import {PageHeadingLevelContext} from './DesktopPageHeader';
import {pageHeaderStyles} from './pageHeaderStyles';

export const PageTitle = styled('h1', {
  display: 'block',
  fontFamily: 'Monument Grotesk Bold',
  fontSize: 24,
  fontWeight: 400,
  lineHeight: 32,
  marginBottom: 24,
  marginTop: 48,
});

export const PageHeader = styled('h2', (_, designColors) =>
  pageHeaderStyles(designColors),
);
export {default as DesktopPageHeader} from './DesktopPageHeader';

const pageSubheaderStyles = (_: object, designColors: ColorMap) => ({
  color: designColors.contentText,
  display: 'block',
  fontFamily: 'Monument Grotesk Bold',
  fontSize: 14,
  fontWeight: 400,
  lineHeight: 20,
  marginBottom: 4,
  marginTop: 0,
});
export const PageSubheader = styled('h3', pageSubheaderStyles, {
  className: 'PageSubheader',
});
/** A `PageSubheader` one level up, for a page whose sections sit directly
 * under its `PageTitle`. It looks the same. */
export const PageSubheaderH2 = styled('h2', pageSubheaderStyles, {
  className: 'PageSubheader',
});

const dashboardTitleStyles = {
  display: 'block',
  fontFamily: 'Monument Grotesk Bold',
  fontSize: 16,
  fontWeight: 400,
  lineHeight: 20,
  marginTop: 0,
  marginBottom: 16,
  ':not(:empty) + .Paragraph': {
    marginTop: -4,
  },
  ':not(:empty) ~ .DashboardTitle': {
    marginTop: 40,
  },
};
const DashboardTitleH3 = styled('h3', dashboardTitleStyles, {
  className: 'DashboardTitle',
});
/** A `DashboardTitle` one level up, for a page whose sections sit directly
 * under its `PageTitle`. It looks the same. */
export const DashboardTitleH2 = styled('h2', dashboardTitleStyles, {
  className: 'DashboardTitle',
});
/**
 * The title of a section of a dashboard page, one level below the page's
 * `DesktopPageHeader`: an `h3` under the page's `h2`, and an `h2` on the
 * account dashboard, where the page's heading is its `h1`
 * (`PageHeadingLevelContext`).
 */
export const DashboardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.ComponentProps<typeof DashboardTitleH3>
>((props, ref) =>
  React.createElement(
    React.useContext(PageHeadingLevelContext) === 1
      ? DashboardTitleH2
      : DashboardTitleH3,
    {...props, ref},
  ),
);
DashboardTitle.displayName = 'DashboardTitle';

const sectionSubheaderStyles = (_: object, designColors: ColorMap) => ({
  color: designColors.headingBlue,
  fontSize: '12px',
  fontWeight: 500,
  marginTop: 0,
  marginBottom: '4px',
  textTransform: 'uppercase' as const,
  ':not(:empty) ~ .SectionSubheader': {
    marginTop: '20px',
  },
});
export const SectionSubheader = styled('h4', sectionSubheaderStyles, {
  className: 'SectionSubheader',
});
/** A `SectionSubheader` one level up, for a section with no `PageSubheader`
 * above it. It looks the same. */
export const SectionSubheaderH3 = styled('h3', sectionSubheaderStyles, {
  className: 'SectionSubheader',
});
/** A `SectionSubheader` two levels up, for a section directly under the
 * page's `h1`. It looks the same. */
export const SectionSubheaderH2 = styled('h2', sectionSubheaderStyles, {
  className: 'SectionSubheader',
});

export const P = styled(
  'p',
  {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 16,
    marginTop: 0,

    '@media (max-width: 400px)': {
      textAlign: 'justify',
    },
    ':not(:empty) + .Paragraph': {
      marginTop: -8,
    },
    ':last-child': {
      marginBottom: 0,
    },
  },
  {className: 'Paragraph'},
);

export const LegalP = styled(
  'p',
  {
    fontSize: 10,
    lineHeight: 13,
    marginBottom: 0,
    marginTop: 0,
    paddingRight: '20%',
    textAlign: 'justify',

    '@media (max-width: 400px)': {
      paddingRight: 0,
    },
    ':not(:empty) + .LegalP': {
      marginTop: 8,
    },
  },
  {className: 'LegalP'},
);

export const TextPill = styled('span', (_, designColors) => ({
  backgroundColor: designColors.contentBackground,
  borderRadius: 3,
  boxShadow: `0 0 0 2px ${designColors.borderGrayAccent}`,
  display: 'inline-flex',
  fontSize: '0.95em',
  margin: '0 2px',
  padding: '0 4px',
}));

export const HelpText = styled('p', {
  fontFamily: DEFAULT_FONT,
  fontSize: 12,
  marginTop: '12px',
  marginRight: 0,
  marginLeft: 0,
  marginBottom: '12px',
});

export const InlineCode = styled('code', (_, designColors) => ({
  // backgroundColor: designColors.background,
  backgroundColor: '#F9F199',
  borderRadius: 4,
  color: '#000',
  display: 'inline-block',
  fontFamily:
    'Monument Grotesk Semi-Mono, Consolas, Input, Courier, Courier New, monospace',
  fontWeight: 400,
  padding: '0 4px',
}));

export const Timestamp = styled('time', (_, designColors) => ({
  color: designColors.contentTextAccent,
  borderBottom: `1px dotted ${designColors.contentTextAccent}`,
  textDecoration: 'none',
}));
